// src/components/Developers/DeveloperView.jsx
import React, { useState, useEffect } from 'react';
import { developersApi, orgsApi } from '../../services/api';
import { Users, Plus, Edit2, Trash2, X, Mail, Clock } from 'lucide-react';

const ROLE_STYLES = {
  admin:     { color: '#f87171', bg: 'rgba(239,68,68,.15)',  label: 'Admin' },
  developer: { color: '#60a5fa', bg: 'rgba(59,130,246,.15)', label: 'Developer' },
  ops:       { color: '#fbbf24', bg: 'rgba(245,158,11,.15)', label: 'Ops' },
  viewer:    { color: '#94a3b8', bg: 'rgba(100,116,139,.2)', label: 'Viewer' },
};
const STATUS_STYLES = {
  active:   { color: '#22c55e', label: 'Active' },
  invited:  { color: '#f59e0b', label: 'Invited' },
  inactive: { color: '#64748b', label: 'Inactive' },
};
const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#22c55e','#f59e0b','#ef4444','#06b6d4','#ec4899','#84cc16'];

const EMPTY_FORM = { name: '', email: '', role: 'developer', orgId: '', status: 'invited', avatarColor: '#3b82f6' };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-lg rounded-xl p-6"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 25px 50px rgba(0,0,0,.5)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} style={{ color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' };

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      {children}
    </div>
  );
}

function formatTime(date) {
  if (!date) return 'Never';
  const diff = Date.now() - new Date(date).getTime();
  if (diff < 60000)         return 'Just now';
  if (diff < 3600000)       return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000)      return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function DeveloperView() {
  const [devs, setDevs]           = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [orgFilter, setOrgFilter]  = useState('ALL');
  const [modal, setModal]          = useState(null);
  const [form, setForm]            = useState(EMPTY_FORM);
  const [saving, setSaving]        = useState(false);
  const [deleteId, setDeleteId]    = useState(null);
  const [search, setSearch]        = useState('');

  async function load() {
    setLoading(true);
    try {
      const [devList, orgList] = await Promise.all([developersApi.list(), orgsApi.list()]);
      setDevs(devList);
      setOrgs(orgList);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openCreate() { setForm(EMPTY_FORM); setModal('create'); }
  function openEdit(dev) {
    setForm({ name: dev.name, email: dev.email, role: dev.role, orgId: String(dev.orgId || ''), status: dev.status, avatarColor: dev.avatarColor || '#3b82f6' });
    setModal(dev);
  }

  async function save() {
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      if (modal === 'create') await developersApi.create(form);
      else await developersApi.update(modal._id, form);
      setModal(null);
      load();
    } finally { setSaving(false); }
  }

  async function confirmDelete(id) {
    await developersApi.delete(id);
    setDeleteId(null);
    load();
  }

  const displayed = devs.filter(d => {
    if (roleFilter !== 'ALL' && d.role !== roleFilter) return false;
    if (orgFilter !== 'ALL' && String(d.orgId) !== orgFilter) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase()) && !d.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const roleCounts = Object.keys(ROLE_STYLES).reduce((acc, r) => ({ ...acc, [r]: devs.filter(d => d.role === r).length }), {});

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <Users size={20} style={{ color: '#60a5fa' }} />
        <div>
          <h1 className="text-base font-semibold">Developers</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Manage team members, roles, and access across organizations</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {Object.entries(ROLE_STYLES).map(([role, s]) => roleCounts[role] > 0 && (
            <span key={role} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.color}30` }}>
              {roleCounts[role]} {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-2 shrink-0 flex items-center gap-2 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
          style={{ ...inputStyle, width: 220, padding: '5px 10px', fontSize: 12 }} />
        <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
          style={{ ...inputStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
          <option value="ALL">All Organizations</option>
          {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
        </select>
        <div className="flex gap-1">
          {['ALL', ...Object.keys(ROLE_STYLES)].map(r => (
            <button key={r} onClick={() => setRoleFilter(r)}
              className="px-2 py-1 rounded text-xs font-medium"
              style={{
                background: roleFilter === r ? 'rgba(59,130,246,.2)' : 'var(--bg-tertiary)',
                color:      roleFilter === r ? '#60a5fa' : 'var(--text-3)',
                border:     `1px solid ${roleFilter === r ? 'rgba(59,130,246,.4)' : 'var(--border)'}`,
                cursor: 'pointer',
              }}>
              {r === 'ALL' ? 'All Roles' : ROLE_STYLES[r].label}
            </button>
          ))}
        </div>
        <button onClick={openCreate} className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
          style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
          <Plus size={14} /> Invite Developer
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                {['Developer', 'Role', 'Organization', 'Status', 'Last Active', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: 'var(--text-3)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((dev, i) => {
                const role   = ROLE_STYLES[dev.role]    || ROLE_STYLES.viewer;
                const status = STATUS_STYLES[dev.status] || STATUS_STYLES.active;
                const org    = orgs.find(o => String(o._id) === String(dev.orgId));
                return (
                  <tr key={dev._id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)' }}
                    className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ background: dev.avatarColor || '#3b82f6' }}>
                          {initials(dev.name)}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{dev.name}</div>
                          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-3)' }}>
                            <Mail size={10} />{dev.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: role.bg, color: role.color }}>{role.label}</span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-2)' }}>{org?.name || '—'}</td>
                    <td className="px-4 py-3">
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, background: `${status.color}18`, color: status.color, border: `1px solid ${status.color}30` }}>{status.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-3)' }}>
                      <span className="flex items-center gap-1"><Clock size={10} />{formatTime(dev.lastActiveAt)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <IconBtn icon={Edit2} onClick={() => openEdit(dev)} />
                        <IconBtn icon={Trash2} onClick={() => setDeleteId(dev._id)} danger />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <Modal title={modal === 'create' ? 'Invite Developer' : 'Edit Developer'} onClose={() => setModal(null)}>
          <Field label="Full Name *">
            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Alex Chen" />
          </Field>
          <Field label="Email Address *">
            <input style={inputStyle} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="alex@example.com" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {Object.entries(ROLE_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Organization">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.orgId} onChange={e => setForm(f => ({ ...f, orgId: e.target.value }))}>
              <option value="">Select organization…</option>
              {orgs.map(o => <option key={o._id} value={String(o._id)}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Avatar Color">
            <div className="flex gap-2 flex-wrap">
              {AVATAR_COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, avatarColor: c }))}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: form.avatarColor === c ? '3px solid white' : '3px solid transparent', cursor: 'pointer', outline: 'none' }} />
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setModal(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={save} disabled={saving || !form.name.trim() || !form.email.trim()}
              style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(59,130,246,.2)', border: '1px solid rgba(59,130,246,.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? .5 : 1 }}>
              {saving ? 'Saving…' : (modal === 'create' ? 'Send Invite' : 'Save')}
            </button>
          </div>
        </Modal>
      )}

      {deleteId && (
        <Modal title="Remove Developer?" onClose={() => setDeleteId(null)}>
          <p className="text-sm mb-5" style={{ color: 'var(--text-2)' }}>This developer will lose all access to the platform. This action cannot be undone.</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleteId(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => confirmDelete(deleteId)} style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(239,68,68,.2)', border: '1px solid rgba(239,68,68,.4)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Remove</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, danger }) {
  return (
    <button onClick={onClick} style={{ padding: 5, borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: danger ? '#f87171' : 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}>
      <Icon size={13} />
    </button>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <Users size={40} style={{ color: 'var(--text-3)' }} />
      <div className="text-sm font-medium">No developers yet</div>
      <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-3)' }}>Invite your first team member to grant them access to the platform.</p>
      <button onClick={onAdd}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
        style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
        <Plus size={14} /> Invite Developer
      </button>
    </div>
  );
}
